import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import logoImg from '@/assets/img/logo.png';

export function PrivacyPolicyPage() {
  // Force dark mode on legal pages
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <div className="min-h-screen bg-[#282836] text-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Back button */}
        <Link to="/">
          <Button variant="ghost" className="mb-8 text-zinc-400 hover:text-white hover:bg-zinc-800">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Button>
        </Link>

        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <img src={logoImg} alt="RoniDoc AI" className="h-10 w-auto" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-zinc-400">Last updated: April 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-zinc max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Data Corner (ABN: 12 345 678 901), trading as RoniDoc AI ("RoniDoc AI", "we", "us", or "our"),
              is committed to protecting the privacy and security of your personal information. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your information when you use our AI-powered
              document intelligence platform ("Service").
            </p>
            <p className="text-zinc-300 leading-relaxed mb-4">
              This Privacy Policy complies with the Privacy Act 1988 (Cth), the Australian Privacy Principles (APPs),
              and other applicable privacy legislation. By using our Service, you consent to the collection and use
              of information in accordance with this policy.
            </p>
            <p className="text-zinc-300 leading-relaxed mb-4">
              If you are located in the European Union, the General Data Protection Regulation (GDPR) may also apply
              to your personal data. We are committed to meeting our obligations under both Australian and European
              privacy frameworks where applicable.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-medium text-white mb-3">2.1 Personal Information You Provide</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We collect personal information that you voluntarily provide when you:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li><strong>Register an account:</strong> Name, email address, password, organisation name, job title</li>
              <li><strong>Use our Service:</strong> Search queries, document interactions, chat conversations with our AI</li>
              <li><strong>Contact us:</strong> Name, email address, phone number, message content</li>
              <li><strong>Subscribe to payments:</strong> Billing address, payment card details (processed by our payment provider)</li>
              <li><strong>Participate in surveys:</strong> Feedback and responses you provide</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">2.2 Information Collected Automatically</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              When you access our Service, we automatically collect certain technical information:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li><strong>Device information:</strong> Device type, operating system, browser type and version</li>
              <li><strong>Network information:</strong> IP address, internet service provider, approximate location</li>
              <li><strong>Usage data:</strong> Pages visited, features used, time spent, clickstream data</li>
              <li><strong>Performance data:</strong> Error logs, load times, system performance metrics</li>
              <li><strong>Authentication data:</strong> Login timestamps, session information, access tokens</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">2.3 Information from Connected Third-Party Services (Xero)</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              When you connect your Xero account to RoniDoc AI, we access the following data on a <strong>read-only</strong> basis:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li><strong>Financial reports:</strong> Profit and Loss statements, Balance Sheets, and Trial Balances</li>
              <li><strong>Contact information:</strong> Client names, ABNs, email addresses, and phone numbers stored in Xero</li>
              <li><strong>Organisation details:</strong> Business name, ABN, and organisation settings</li>
              <li><strong>Account settings:</strong> Chart of accounts and tax rate configurations</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              <strong>Important:</strong> RoniDoc AI requests <strong>read-only access</strong> to your Xero data. We cannot and do not
              create, modify, or delete any data in your Xero account. We do not access bank account details, payment information,
              or any write-capable Xero API scopes. Xero data is fetched on-demand when you use features that require it and
              is not stored permanently beyond encrypted OAuth tokens required to maintain your connection.
            </p>

            <h3 className="text-xl font-medium text-white mb-3">2.4 Documents and Content You Upload</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              When you upload documents to our knowledge base, we collect and process:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>The document content including text, images, and metadata</li>
              <li>File names, sizes, types, and upload timestamps</li>
              <li>AI-generated embeddings, topics, and summaries derived from your documents</li>
              <li>Your queries and interactions with the uploaded content</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              <strong>Important:</strong> You retain full ownership of all documents you upload. We process this
              content solely to provide our Service and do not use it for any other purpose without your explicit consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>

            <h3 className="text-xl font-medium text-white mb-3">3.1 To Provide and Maintain Our Service</h3>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Create and manage your user account</li>
              <li>Process and store your uploaded documents</li>
              <li>Generate AI-powered search results, summaries, and insights</li>
              <li>Enable document sharing and collaboration features</li>
              <li>Provide customer support and respond to inquiries</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">3.2 To Improve and Personalise the Service</h3>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Analyse usage patterns to improve features and functionality</li>
              <li>Develop new products and services</li>
              <li>Personalise your experience based on your preferences</li>
              <li>Conduct research and analytics to enhance our AI capabilities</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">3.3 To Communicate With You</h3>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Send service-related notifications and updates</li>
              <li>Respond to your enquiries and support requests</li>
              <li>Send marketing communications (with your consent)</li>
              <li>Notify you of changes to our Terms or Privacy Policy</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">3.4 To Ensure Security and Compliance</h3>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Detect and prevent fraud, abuse, and security threats</li>
              <li>Monitor and enforce compliance with our Terms of Service</li>
              <li>Comply with legal obligations and respond to lawful requests</li>
              <li>Protect the rights, property, and safety of our users</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">4. AI Processing and Third-Party Services</h2>

            <h3 className="text-xl font-medium text-white mb-3">4.1 How AI Processes Your Data</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              RoniDoc AI uses artificial intelligence to provide document analysis and chat capabilities.
              When you use our Service:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Your documents are processed to extract text and generate searchable embeddings</li>
              <li>Your queries are sent to AI models to generate relevant responses</li>
              <li>Document metadata and topics are automatically classified</li>
              <li>Images within documents may be analysed for content understanding</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">4.2 Third-Party AI Providers</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We use the following third-party AI services to power our platform:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li><strong>OpenAI:</strong> For document embeddings (text-embedding-3-small) and optional chat (GPT-4o)</li>
              <li><strong>Anthropic (Claude):</strong> For advanced language understanding and generation</li>
              <li><strong>Google (Gemini):</strong> For optional AI chat capabilities</li>
              <li><strong>xAI (Grok):</strong> For optional AI chat capabilities</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              These providers process your data under strict contractual agreements that require them to:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Process data only for the purpose of providing services to us</li>
              <li>Not use your data to train or improve their AI models</li>
              <li>Implement appropriate security measures</li>
              <li>Delete data after processing is complete</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">4.3 Data Training Opt-Out</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              <strong>Your data is NOT used to train AI models.</strong> We have disabled all data training
              options with our AI providers. Your documents and queries are processed solely to generate
              responses for you and are not retained or used for model improvement.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">5. Data Storage and Security</h2>

            <h3 className="text-xl font-medium text-white mb-3">5.1 Where We Store Your Data</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              All data is stored exclusively in Australia using Amazon Web Services (AWS) infrastructure:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li><strong>Region:</strong> AWS Asia Pacific (Sydney) ap-southeast-2 - all infrastructure resides in Australia</li>
              <li><strong>Document storage:</strong> Amazon S3 with SSE-S3 server-side encryption (Sydney region)</li>
              <li><strong>Database:</strong> Amazon RDS PostgreSQL 15 with AES-256 encryption at rest via AWS KMS (Sydney region)</li>
              <li><strong>Authentication:</strong> AWS Cognito user pool hosted in Sydney region</li>
              <li><strong>API processing:</strong> AWS Lambda and API Gateway in Sydney region</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">5.2 Security Measures</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We implement comprehensive security measures to protect your data:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li><strong>Encryption in transit:</strong> HTTPS/TLS 1.2+ enforced for all data transmission</li>
              <li><strong>Encryption at rest:</strong> AES-256 encryption for database and document storage via AWS KMS</li>
              <li><strong>OAuth token encryption:</strong> Xero access and refresh tokens are encrypted using AES-256-CBC with unique per-record initialisation vectors before storage</li>
              <li><strong>Tenant isolation:</strong> PostgreSQL Row-Level Security (RLS) ensures no cross-tenant data access is possible</li>
              <li><strong>Access controls:</strong> Role-based access control (super admin, tenant admin, manager, user) with JWT-based authentication</li>
              <li><strong>Authentication:</strong> AWS Cognito with secure password hashing and session management</li>
              <li><strong>Monitoring:</strong> AWS CloudWatch for real-time logging, monitoring, and alerting</li>
              <li><strong>Auditing:</strong> Comprehensive audit logs for all data access, queries, exports, and integrations</li>
              <li><strong>Backups:</strong> Daily automated database backups with point-in-time recovery</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">5.3 Data Breach Response</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              In the event of a data breach, we will:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Notify the Office of the Australian Information Commissioner (OAIC) within 72 hours if required</li>
              <li>Notify affected individuals as soon as practicable</li>
              <li>Take immediate steps to contain and remediate the breach</li>
              <li>Provide recommendations for protective measures you can take</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">6. Data Sharing and Disclosure</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We do not sell, rent, or trade your personal information. We may share your information in the
              following circumstances:
            </p>

            <h3 className="text-xl font-medium text-white mb-3">6.1 Service Providers</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We share data with trusted third-party service providers who assist us in operating our Service:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li><strong>Cloud infrastructure:</strong> Amazon Web Services (AWS) - Sydney, Australia</li>
              <li><strong>AI processing:</strong> OpenAI, Anthropic, Google, xAI (configurable per tenant)</li>
              <li><strong>Accounting integration:</strong> Xero (read-only OAuth 2.0 connection)</li>
              <li><strong>Authentication:</strong> AWS Cognito</li>
              <li><strong>Email delivery:</strong> Amazon SES</li>
              <li><strong>Payment processing:</strong> Stripe (we do not store payment card details)</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">6.2 Legal Requirements</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We may disclose your information when required by law or in response to:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Valid legal process (court orders, subpoenas, warrants)</li>
              <li>Government requests from authorised agencies</li>
              <li>To protect our legal rights and interests</li>
              <li>To prevent fraud or other illegal activity</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">6.3 Business Transfers</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              If we are involved in a merger, acquisition, or sale of assets, your information may be
              transferred as part of that transaction. We will notify you of any change in ownership or
              use of your personal information.
            </p>

            <h3 className="text-xl font-medium text-white mb-3">6.4 With Your Consent</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We may share your information with third parties when you have given us explicit consent to do so.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">7. International Data Transfers</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              While our primary data storage is in Australia, some of our service providers operate globally.
              When your data is transferred overseas, we ensure appropriate safeguards are in place:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>We only transfer data to countries with adequate privacy protections or to providers with
                  appropriate contractual safeguards</li>
              <li>AWS and our AI providers maintain compliance with international data protection standards</li>
              <li>We use Standard Contractual Clauses (SCCs) where required for transfers to third countries</li>
              <li>You can request information about the safeguards in place for international transfers</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">8. Xero Integration and Data Handling</h2>

            <h3 className="text-xl font-medium text-white mb-3">8.1 How We Connect to Xero</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              RoniDoc AI integrates with Xero using the industry-standard OAuth 2.0 protocol. When you choose
              to connect your Xero account:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>You are redirected to Xero's login page to authenticate and authorise access</li>
              <li>You select which Xero organisations to connect</li>
              <li>Xero issues access and refresh tokens which we store encrypted in our database</li>
              <li>We request only <strong>read-only scopes</strong>: accounting.transactions.read, accounting.reports.read, accounting.contacts.read, and accounting.settings.read</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">8.2 Xero Data We Access</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We access the following Xero data on-demand (not via background sync) to power our AI analysis features:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Profit and Loss reports for compliance checking and tax planning</li>
              <li>Balance Sheet reports for financial analysis</li>
              <li>Trial Balance for audit and reconciliation tools</li>
              <li>Contact records for client matching (ABN auto-matching)</li>
              <li>Organisation details for business identification</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">8.3 What We Do NOT Do with Xero Data</h3>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>We do NOT write, create, modify, or delete any data in your Xero account</li>
              <li>We do NOT access bank account details or payment instruments</li>
              <li>We do NOT store Xero financial data permanently; it is fetched on-demand for each analysis</li>
              <li>We do NOT share Xero data with any third parties beyond AI processing providers for generating insights</li>
              <li>We do NOT use Xero data to train AI models</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">8.4 Xero Token Security</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              OAuth tokens issued by Xero are stored with the following protections:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Encrypted using AES-256-CBC with a unique random initialisation vector per token</li>
              <li>Stored in our PostgreSQL database in Sydney, Australia (ap-southeast-2)</li>
              <li>Access tokens are automatically refreshed before expiry; expired tokens are never retained</li>
              <li>Tokens are tenant-isolated via Row-Level Security; no cross-tenant access is possible</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">8.5 Disconnecting from Xero</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              You can disconnect your Xero account at any time from the Integrations page. When you disconnect:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Your Xero connection is revoked via the Xero API</li>
              <li>All stored OAuth tokens are permanently deleted from our database</li>
              <li>Xero organisation mappings are cleared from your client records</li>
              <li>No Xero data is retained after disconnection</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">9. Cookies and Tracking Technologies</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We use cookies and similar technologies to enhance your experience:
            </p>

            <h3 className="text-xl font-medium text-white mb-3">8.1 Types of Cookies We Use</h3>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li><strong>Essential cookies:</strong> Required for authentication and security (cannot be disabled)</li>
              <li><strong>Functional cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Analytics cookies:</strong> Help us understand how you use our Service</li>
              <li><strong>Performance cookies:</strong> Monitor Service performance and identify issues</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">8.2 Managing Cookies</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              You can manage your cookie preferences through:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Our cookie consent banner (shown when you first visit)</li>
              <li>Your browser settings</li>
              <li>The settings page in your account</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Note that disabling certain cookies may affect the functionality of our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">10. Data Retention</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We retain your data for as long as necessary to provide our Service and comply with legal obligations:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li><strong>Account data:</strong> Retained while your account is active</li>
              <li><strong>Documents:</strong> Retained until you delete them or close your account</li>
              <li><strong>Chat history:</strong> Retained for 12 months, then automatically deleted</li>
              <li><strong>Access logs:</strong> Retained for 90 days for security purposes</li>
              <li><strong>Billing records:</strong> Retained for 7 years as required by Australian tax law</li>
              <li><strong>Xero OAuth tokens:</strong> Retained while your Xero connection is active; permanently deleted upon disconnection</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Upon account deletion, we will:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Delete your personal data within 30 days</li>
              <li>Permanently delete your uploaded documents within 30 days</li>
              <li>Retain anonymised usage data for analytics purposes</li>
              <li>Retain data required for legal compliance</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">11. Your Privacy Rights</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Under Australian privacy law and other applicable regulations, you have the following rights:
            </p>

            <h3 className="text-xl font-medium text-white mb-3">11.1 Access and Correction</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Request access to the personal information we hold about you</li>
              <li>Request correction of inaccurate or incomplete information</li>
              <li>Receive a copy of your data in a portable format</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">11.2 Deletion and Restriction</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Request deletion of your personal data (subject to legal retention requirements)</li>
              <li>Request restriction of processing in certain circumstances</li>
              <li>Object to processing based on legitimate interests</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">11.3 Consent Withdrawal</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Where we process your data based on consent, you have the right to:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Withdraw consent at any time</li>
              <li>Opt out of marketing communications</li>
              <li>Manage cookie preferences</li>
            </ul>

            <h3 className="text-xl font-medium text-white mb-3">11.4 How to Exercise Your Rights</h3>
            <p className="text-zinc-300 leading-relaxed mb-4">
              To exercise any of these rights, you can:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Use the settings in your account dashboard</li>
              <li>Email us at <a href="mailto:privacy@ronidoc.com.au" className="text-primary hover:underline">privacy@ronidoc.com.au</a></li>
              <li>Submit a request through our support portal</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We will respond to your request within 30 days. We may need to verify your identity before
              processing certain requests.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">12. Children's Privacy</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Our Service is not intended for use by individuals under the age of 18. We do not knowingly
              collect personal information from children. If you are a parent or guardian and believe your
              child has provided us with personal information, please contact us immediately at{' '}
              <a href="mailto:privacy@ronidoc.com.au" className="text-primary hover:underline">privacy@ronidoc.com.au</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">13. Third-Party Links</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Our Service may contain links to third-party websites or services. We are not responsible for
              the privacy practices of these external sites. We encourage you to review the privacy policies
              of any third-party services you access through our platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">14. Changes to This Privacy Policy</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time to reflect changes in our practices or
              for legal, operational, or regulatory reasons. When we make changes:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>We will update the "Last updated" date at the top of this policy</li>
              <li>For significant changes, we will notify you via email or through the Service</li>
              <li>We encourage you to review this policy periodically</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Your continued use of the Service after any changes indicates your acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">15. Complaints</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              If you believe we have breached your privacy rights, you can:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 ml-4 mb-4">
              <li>Contact us first at <a href="mailto:privacy@ronidoc.com.au" className="text-primary hover:underline">privacy@ronidoc.com.au</a></li>
              <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at{' '}
                <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.oaic.gov.au</a>
              </li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mb-4">
              We take all complaints seriously and will work to resolve any issues promptly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">16. Contact Us</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="text-zinc-300 space-y-2">
              <p><strong className="text-white">Data Corner</strong> (trading as RoniDoc AI)</p>
              <p><strong className="text-white">Privacy Officer</strong></p>
              <p>Sydney, NSW, Australia</p>
              <p>Email: <a href="mailto:privacy@ronidoc.com.au" className="text-primary hover:underline">privacy@ronidoc.com.au</a></p>
              <p>General enquiries: <a href="mailto:support@ronidoc.com.au" className="text-primary hover:underline">support@ronidoc.com.au</a></p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-zinc-800 text-center text-sm text-zinc-500">
          &copy; {new Date().getFullYear()} Data Corner trading as RoniDoc AI. All rights reserved.
        </div>
      </div>
    </div>
  );
}
